import React from 'react';
import { ColumnDef, flexRender, getCoreRowModel, getFilteredRowModel, RowData, useReactTable } from '@tanstack/react-table';

declare module '@tanstack/table-core' {
	interface ColumnMeta<TData extends RowData, TValue> {
		width?: number;
		columnClass?: string;
		headerClass?: string;
		displayMode?: 'tb-col-xxl' | 'tb-col-lg' | 'tb-col-md' | 'tb-col-mb' | 'tb-col-xs';
	}
}

type ITableProps<TData> = {
	data: TData[];
	columns: ColumnDef<TData>[];
	refresh: () => void;
	keyFn: string;
};

export const TableDefault = (props: ITableProps<any>) => {
	const { columns, data, refresh } = props;

	const initialColumns = React.useMemo<ColumnDef<any>[]>(() => {
		let result: ColumnDef<any>[] = [
			{
				id: 'tbl_idx',
				header: () => 'STT',
				cell: ({ row }) => <span>{row.index + 1}</span>,
				meta: { headerClass: 'text-center', columnClass: 'text-center', width: 80 },
			},
		];

		result = [...result, ...columns];
		return result;
	}, [columns]);

	const table = useReactTable<any>({
		data,
		columns: initialColumns,
		enableRowSelection: true,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	});

	return (
		<div className='space-y-4'>
			<div className='flex justify-end'>
				<button
					className='inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-muted'
					type='button'
					onClick={() => refresh()}
				>
					<span>Làm mới</span>
				</button>
			</div>

			<div className='overflow-x-auto'>
				<table className='w-full border-collapse overflow-hidden rounded-lg border bg-card'>
					<thead>
						{table.getHeaderGroups().map((headerGroup) => (
							<tr key={headerGroup.id} className='border-b bg-muted/30'>
								{headerGroup.headers.map((header) => {
									const config = header.column.columnDef.meta;
									const width = config ? `${config?.width}px` : undefined;
									return (
										<th
											key={header.id}
											colSpan={header.colSpan}
											style={{ width: width || 'unset' }}
											className={`px-3 py-2 text-left text-sm font-semibold text-foreground ${config?.headerClass ?? ''} ${config?.displayMode ?? ''}`}
										>
											{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
										</th>
									);
								})}
							</tr>
						))}
					</thead>

					<tbody>
						{table.getRowModel().rows.map((row) => (
							<tr key={row.id} className='border-b align-top hover:bg-muted/20'>
								{row.getVisibleCells().map((cell) => {
									const config = cell.column.columnDef.meta;
									const width = config ? `${config?.width}px` : undefined;
									return (
										<td
											key={cell.id}
											style={{ width: width || 'unset' }}
											className={`px-3 py-2 text-sm ${config?.columnClass ?? ''} ${config?.displayMode ?? ''}`}
										>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</td>
									);
								})}
							</tr>
						))}
					</tbody>

					<tfoot>
						<tr className='bg-muted/20'>
							<td className='px-3 py-2 text-sm text-muted-foreground' colSpan={table.getAllLeafColumns().length - 1}>
								Dữ liệu trang hiện tại (<b>{table.getRowModel().rows.length}</b>)
							</td>
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
};
